import React, { useMemo } from 'react';
import SegmentedDistributionBar from 'components/AiAnalytics/shared/SegmentedDistributionBar/SegmentedDistributionBar';
import DataNotFound from 'components/DataNotFound/DataNotFound';
import { BaseWidget } from 'models/WidgetModel';
import { mapConversationBalanceResponse } from './conversationBalance.mapper';
import { formatConversationBalanceRawValue } from './conversationBalance.formatters';
import './ConversationBalance.scss';

const formatShare = (share: number) => `${Number.isInteger(share) ? share : share.toFixed(1)}%`;

const ConversationBalance: React.FC<BaseWidget> = ({ response }) => {
  const metrics = useMemo(() => mapConversationBalanceResponse(response), [response]);

  if (!metrics) return <DataNotFound />;

  return (
    <div className="conversation-balance">
      {metrics.map((metric) => {
        const unit = metric.unit;
        const accessibleLabel = `${metric.label}: ${metric.segments
          .map((segment) => {
            const rawValue =
              unit && segment.rawValue !== undefined
                ? formatConversationBalanceRawValue(segment.rawValue, unit, metric.key)
                : undefined;
            return rawValue
              ? `${segment.label} ${rawValue} (${formatShare(segment.value)})`
              : `${segment.label} ${formatShare(segment.value)}`;
          })
          .join(', ')}`;

        return (
          <section className="conversation-balance__metric" key={metric.key}>
            <div className="conversation-balance__metric-header">
              <h3 className="conversation-balance__metric-title">{metric.label}</h3>
              {unit ? (
                <div className="conversation-balance__metric-values">
                  {metric.segments.map((segment) => (
                    <span
                      className="conversation-balance__metric-value"
                      data-testid={`conversation-balance-raw-value-${segment.id}`}
                      key={segment.id}
                    >
                      {segment.rawValue !== undefined ? (
                        <>
                          <strong style={{ color: segment.color }}>
                            {formatConversationBalanceRawValue(segment.rawValue, unit, metric.key)}
                          </strong>{' '}
                          <span>{segment.label.toLowerCase()}</span>
                        </>
                      ) : null}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            {metric.hasActivity ? (
              <SegmentedDistributionBar
                segments={metric.segments}
                accessibleLabel={accessibleLabel}
              />
            ) : (
              <div className="conversation-balance__no-activity">No activity</div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default ConversationBalance;
